import Header from '../components/layout-components/Header';
import SideBarMenu from '../components/layout-components/SideBarMenu';
import GlobalSOSAlert from '../components/ui-components/GlobalSOSAlert';
import ResetAlertPopup from '../components/ui-components/ResetAlertPopup';
import AlertPopup from '../components/ui-components/AlertPopup';
import { Flex } from '@radix-ui/themes';
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { useEffect, useState, useRef } from 'react';
import { permissions } from '../config/permissions';
import { notificationService } from '../services/NotificationService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function AppLayout() {
    const { authenticated, authority, activeAlerts, clearAllAlerts, removeProcessedAlerts, alarmSettings, setActiveAlerts } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeSosAlert, setActiveSosAlert] = useState(null); // Track active SOS alert
    const [activeResetAlert, setActiveResetAlert] = useState(null); // Track active Reset alert
    const [notificationPermissionGranted, setNotificationPermissionGranted] = useState(false);
    const [isAppMinimized, setIsAppMinimized] = useState(false);
    const processedAlertsRef = useRef(new Set());
    const dismissedResetIdsRef = useRef(new Set()); // Track dismissed reset alert IDs persistently
    const dismissedSosIdsRef = useRef(new Set()); // Track dismissed SOS alert IDs persistently

    const currentUserId = parseInt(localStorage.getItem('userId'));

    // Initialize notification service and request permissions
    useEffect(() => {
        const initializeNotifications = async () => {
            console.log('Initializing notification service...');

            // Setup notification handlers
            notificationService.setupNotificationHandlers();

            // Request notification permission
            const hasPermission = await notificationService.requestPermission();
            setNotificationPermissionGranted(hasPermission);

            if (hasPermission) {
                console.log('Notification permission granted');

                // Show a test notification to confirm it's working
                // await notificationService.showNotification('Patient Alarm System', {
                //     body: 'Notifications are now enabled for this app',
                //     silent: true,
                //     requireInteraction: false
                // });
            } else {
                console.warn('Notification permission denied');
            }
        };

        if (authenticated) {
            initializeNotifications();
        }
    }, [authenticated]);

    // Track app visibility for notifications
    useEffect(() => {
        const handleVisibilityChange = () => {
            const isHidden = document.hidden;
            setIsAppMinimized(isHidden);

            console.log(isHidden ? '📱 App minimized/hidden' : '📱 App visible/focused');

            if (!isHidden) {
                // App became visible - clear all notifications
                notificationService.clearAllAlarmNotifications();
            }
        };

        const handleFocus = () => {
            setIsAppMinimized(false);
            console.log('📱 App focused');
            notificationService.clearAllAlarmNotifications();
        };

        const handleBlur = () => {
            setIsAppMinimized(true);
            console.log('📱 App blurred');
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        // Initial state
        setIsAppMinimized(document.hidden);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    useEffect(() => {
        if (authenticated) {
            console.log('AppLayout useEffect:', authenticated, Date.now());
            // navigate(`/${permissions?.[authority]?.[0]?.path}`);
        } else {
            navigate('/auth/login')
        }
    }, [])

    // Process SignalR alerts for SOS and Reset only
    useEffect(() => {
        if (activeAlerts.length > 0) {
            // Filter out alerts we've already processed
            const newAlerts = activeAlerts.filter(alert => !processedAlertsRef.current.has(alert.id));
            if (newAlerts.length === 0) return;

            console.log('AppLayout - SignalR - Processing new alerts:', newAlerts.length);

            // Sort new alerts by timestamp (oldest first)
            const sortedAlerts = [...newAlerts].sort((a, b) => {
                const timeA = new Date(a.timestamp || a["0"]?.timestamp || 0).getTime();
                const timeB = new Date(b.timestamp || b["0"]?.timestamp || 0).getTime();
                return timeA - timeB; // Oldest first
            });

            // Process each alert
            sortedAlerts.forEach((alert) => {
                const alertType = alert["0"]?.alertType || alert.alertType;

                if (alertType === 'SOS') {
                    processSOSAlert(alert);
                } else if (alertType === 'Reset') {
                    processResetAlert(alert);
                } else if (alertType && !['Confirmation', 'Hold', 'HoldExtension', 'SensorStatus'].includes(alertType)) {
                    // Handle regular alarms for notifications when app is minimized
                    if (isAppMinimized && notificationPermissionGranted) {
                        processAlarmForNotification(alert);
                    }
                }

                // Mark as processed regardless of type, so we don't look at it again
                processedAlertsRef.current.add(alert.id);
            });

            // Trim the set to prevent memory leaks over very long sessions
            if (processedAlertsRef.current.size > 200) {
                const idsArray = Array.from(processedAlertsRef.current);
                processedAlertsRef.current = new Set(idsArray.slice(-100));
            }
        }
    }, [activeAlerts, isAppMinimized, notificationPermissionGranted]);

    const processAlarmForNotification = (alert) => {
        console.log('Processing alarm for notification:', alert);

        // Validate alert structure
        if (!alert || typeof alert !== 'object') {
            console.warn('Invalid alarm alert received:', alert);
            return;
        }

        // Extract alert data (handle wrapped alerts)
        let actualAlert = alert;
        if (alert["0"] && typeof alert["0"] === 'object') {
            actualAlert = alert["0"];
        }

        const { alertType, payload } = actualAlert;

        if (!payload || !payload.masterAlarmId) {
            console.warn('Alarm alert missing required payload data');
            return;
        }

        // Find alarm configuration
        const alarmConfig = alarmSettings.find(alarm => alarm.masterAlarmId === payload.masterAlarmId);
        if (!alarmConfig) {
            console.warn('No alarm configuration found for masterAlarmId:', payload.masterAlarmId);
            return;
        }

        // Create notification data
        const notificationData = {
            alarmName: alarmConfig.alarmName || alertType,
            roomNumber: payload.roomId || 'Unknown',
            bedId: payload.bedId || 'Unknown',
            priority: payload.alarmPriority || 3,
            color: alarmConfig.color || '#ff6b6b'
        };

        console.log('Showing alarm notification:', notificationData);

        // Show notification
        notificationService.showAlarmNotification(notificationData);
    };

    const processSOSAlert = (alert) => {
        console.log('AppLayout - Processing SOS alert:', alert);

        if (!alert || typeof alert !== 'object') {
            console.warn('Invalid SOS alert received:', alert);
            return;
        }

        let actualAlert = alert;
        if (alert["0"] && typeof alert["0"] === 'object') {
            console.log('SOS Alert is wrapped, extracting actual alert data');
            actualAlert = alert["0"];
        }

        const { alertType, payload } = actualAlert;

        if (alertType === 'SOS') {
            // If this SOS alert was already dismissed by the user, skip it completely
            if (dismissedSosIdsRef.current.has(actualAlert.id)) {
                console.log('SOS alert already dismissed, skipping:', actualAlert.id);
                return;
            }

            // Do not show SOS to the user who triggered it
            const triggerUserId = payload?.UserId || payload?.userId;
            if (triggerUserId && String(triggerUserId) === String(currentUserId)) {
                console.log('🆘 SOS triggered by current user — suppressing for self');
                return;
            }

            handleSOSAlert(actualAlert);

            // Show SOS notification if app is minimized
            if (isAppMinimized && notificationPermissionGranted) {
                console.log('Showing SOS notification');
                notificationService.showSOSNotification(payload || {});
            }
        }
    };

    const processResetAlert = (alert) => {
        console.log('AppLayout - Processing Reset alert:', alert);

        if (!alert || typeof alert !== 'object') {
            console.warn('Invalid Reset alert received:', alert);
            return;
        }

        let actualAlert = alert;
        if (alert["0"] && typeof alert["0"] === 'object') {
            console.log('Reset Alert is wrapped, extracting actual alert data');
            actualAlert = alert["0"];
        }

        const { alertType, payload } = actualAlert;

        if (alertType === 'Reset' && payload) {
            // If this reset alert was already dismissed by the user, skip it completely
            if (dismissedResetIdsRef.current.has(actualAlert.id)) {
                console.log('Reset alert already dismissed, skipping:', actualAlert.id);
                return;
            }

            const alertUserId = payload.userId;

            console.log('🔍 Reset alert userId check:', {
                alertUserId,
                currentUserId,
                match: alertUserId == currentUserId
            });

            if (alertUserId == currentUserId) {
                console.log('Reset alert is for current user, showing popup');
                handleResetAlert(actualAlert);
            } else {
                console.log('❌ Reset alert is not for current user, ignoring');
            }
        }
    };

    const handleSOSAlert = (sosAlert) => {
        console.log('AppLayout - Handling SOS alert:', sosAlert);

        // Don't allow multiple SOS alerts
        if (activeSosAlert) {
            console.log('⚠️ SOS alert already active, ignoring new SOS alert');
            return;
        }

        setActiveSosAlert(sosAlert);
    };

    const handleResetAlert = (resetAlert) => {
        console.log('AppLayout - Handling Reset alert:', resetAlert);

        // Don't allow multiple reset alerts - but SOS has priority
        if (activeResetAlert) {
            console.log('⚠️ Reset alert already active, ignoring new Reset alert');
            return;
        }

        // SOS has priority over reset alerts
        if (activeSosAlert) {
            console.log('SOS alert active, deferring Reset alert');
            return;
        }

        setActiveResetAlert(resetAlert);
    };

    const handleDismissSOS = () => {
        console.log('AppLayout - Dismissing SOS alert');
        
        if (activeSosAlert) {
            const alertId = activeSosAlert.id;
            if (alertId) {
                dismissedSosIdsRef.current.add(alertId);
            }

            // Also remove it from global activeAlerts so audio and other effects stop
            if (setActiveAlerts) {
                setActiveAlerts((prev) =>
                    prev.filter((a) => a.id !== alertId)
                );
            }
        }
        
        setActiveSosAlert(null);

        // Clear SOS notifications
        if (notificationPermissionGranted) {
            notificationService.clearNotifications('sos-emergency');
        }

        // If there was a deferred reset alert, show it now
        // (This would require additional logic to store deferred alerts if needed)
    };

    const handleDismissReset = () => {
        console.log('AppLayout - Dismissing Reset alert');

        // Mark this reset alert as dismissed so it never re-appears, even if activeAlerts changes
        if (activeResetAlert) {
            const alertId = activeResetAlert.id;
            if (alertId) {
                dismissedResetIdsRef.current.add(alertId);
            }

            // Also remove it from global activeAlerts so audio and other effects stop
            if (setActiveAlerts) {
                setActiveAlerts((prev) =>
                    prev.filter((a) => a.id !== alertId)
                );
            }
        }

        setActiveResetAlert(null);
    };

    // Check if we should show AlertPopup
    const shouldShowAlertPopup = () => {
        // Don't show if SOS or Reset alerts are active
        if (activeSosAlert || activeResetAlert) {
            return false;
        }

        // Don't show if on patient pages (they handle their own alerts)
        const isOnPatientPage = location.pathname.includes('/my-patients') ||
            location.pathname.includes('/special-role-my-patients');

        return !isOnPatientPage;
    };

    return (
        <>
            {/* Notification Permission Status Indicator (for debugging) */}
            {/*process.env.NODE_ENV === 'development' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    background: notificationPermissionGranted ? 'green' : 'red',
                    color: 'white',
                    padding: '4px 8px',
                    fontSize: '12px',
                    zIndex: 10000
                }}>
                    Notifications: {notificationPermissionGranted ? 'ON' : 'OFF'} | 
                    App: {isAppMinimized ? 'MINIMIZED' : 'VISIBLE'}
                </div>
            )*/}

            {/* Global SOS Alert Overlay - Shows over entire app (Priority 1) */}
            <GlobalSOSAlert
                sosAlert={activeSosAlert}
                onDismiss={handleDismissSOS}
            />

            {/* Reset Alert Popup - Shows when no SOS alert is active (Priority 2) */}
            {!activeSosAlert && (
                <ResetAlertPopup
                    resetAlert={activeResetAlert}
                    onDismiss={handleDismissReset}
                />
            )}

            {/* Alert Popup - Shows when no SOS or Reset alerts are active and not on patient pages (Priority 3) */}
            {shouldShowAlertPopup() && (
                <AlertPopup />
            )}

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />

            <div className='w-full h-screen overflow-hidden'>
                <Flex direction={'row'} className="relative">
                    <SideBarMenu />
                    <div className='h-screen flex flex-col w-full'>
                        <Header />
                        <div className="pt-7 px-4 h-screen overflow-y-auto bg-[#e8e8e8]">
                            <Outlet />
                        </div>
                    </div>
                </Flex>
            </div>
        </>
    );
}

export default AppLayout;